import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { useLayoutEffect, useState } from 'react';
import useSearchOptionsDropdownLogic, {
  HookProps,
} from './useSearchOptionsDropdownLogic';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    checkboxGroup: {
      background: theme.palette.common.white,
      borderRadius: '0 0 4px 4px',
      borderTop: `solid 1px ${theme.palette.grey[300]}`,
      boxShadow: '0px 3px 5px -2px rgba(0, 0, 0, 0.2)',
      margin: 0,
      paddingBottom: 1,
      position: 'absolute',
      width: '100%',
      zIndex: 2,
      bottom: -76,
    },
    control: {
      display: 'block',
    },
    checkbox: {
      marginTop: 1,
      marginLeft: 6,
      paddingRight: 4,
    },
    checkboxLabel: {
      color: theme.palette.coolGrey[700],
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 'normal',
    },
    hide: {
      display: 'none',
    },
    dropdownBackgroundContainer: {
      width: '100%',
      zIndex: -1,
      boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.25)',
      borderRadius: '0px 0px 6px 6px',
      position: 'absolute',
      minHeight: `calc(100% + ${theme.spacing(0.5)}px)`,
      minWidth: `calc(100% + ${theme.spacing(2)}px)`,
      top: '80px',
      left: '-8px',
      overflow: 'hidden',
    },
    dropdownBackgroundGradient: {
      background: theme.palette.gradient.queryAnywhere,
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      width: '100vw',
      zIndex: 1,
    },
  })
);

export interface SearchOptionsDropdownProps extends HookProps {
  hidden: boolean;
  includeDeleted: boolean;
  autocompleteEnabled: boolean;
  labelOnClick: (event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => void;
  showGradientBackground: boolean;
}

const SearchOptionsDropdown: React.FC<SearchOptionsDropdownProps> = ({
  hidden,
  includeDeleted,
  autocompleteEnabled,
  labelOnClick,
  showGradientBackground,
  ...hookProps
}) => {
  const classes = useStyles();
  const [
    gradientBackgroundLeftPercentage,
    setGradientBackgroundLeftPercentage,
  ] = useState(0);
  const {
    handleDeletedCheckbox,
    handleAutocompleteCheckbox,
    calculateLeftAlignPercentageForGradientBorder,
  } = useSearchOptionsDropdownLogic(hookProps);

  useLayoutEffect(() => {
    const resizeListener = () => {
      const leftPercentageForLinearGradientBackground = calculateLeftAlignPercentageForGradientBorder();
      setGradientBackgroundLeftPercentage(
        leftPercentageForLinearGradientBackground
      );
    };
    resizeListener();
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, []);

  return (
    <>
      {gradientBackgroundLeftPercentage &&
      gradientBackgroundLeftPercentage !== 0 ? (
        <div
          className={clsx(
            classes.dropdownBackgroundContainer,
            hidden && classes.hide,
            !showGradientBackground && classes.hide
          )}
          id="dropdownBackgroundContainer"
        >
          <div
            className={classes.dropdownBackgroundGradient}
            style={{
              left: `-${gradientBackgroundLeftPercentage}%`,
            }}
          />
        </div>
      ) : null}
      <div className={clsx(classes.checkboxGroup, hidden && classes.hide)}>
        <FormControlLabel
          className={clsx(classes.control)}
          classes={{
            label: classes.checkboxLabel,
          }}
          control={
            <Checkbox
              color="primary"
              checked={includeDeleted}
              onChange={handleDeletedCheckbox}
              value="includeDeletedEntities"
              classes={{
                root: classes.checkbox,
              }}
            />
          }
          label="Include recently deleted entities in query/search"
          onClick={labelOnClick}
          aria-label="Include Deleted"
        />

        <FormControlLabel
          className={clsx(classes.control, hidden && classes.hide)}
          classes={{
            label: classes.checkboxLabel,
          }}
          control={
            <Checkbox
              color="primary"
              checked={autocompleteEnabled}
              onChange={handleAutocompleteCheckbox}
              value="enableAutocomplete"
              classes={{
                root: classes.checkbox,
              }}
            />
          }
          label="Enable autocomplete for J1QL"
          onClick={labelOnClick}
          aria-label="Enable Autocomplete"
        />
      </div>
    </>
  );
};

export default SearchOptionsDropdown;
